import { AlertTriangle, MapPinOff, Bug, LogOut } from 'lucide-react';
import type { PainPointsResult } from '../../../../api/widgetTelemetry';
import StatCard from '../shared/StatCard';

interface PainPointsProps {
  data: PainPointsResult | null;
  loading: boolean;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

export default function PainPoints({ data, loading }: PainPointsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Zero-result Rate"
          value={pct(data.zeroResultRate)}
          icon={AlertTriangle}
          iconColor="text-amber-600"
        />
        <StatCard
          label="Geo Failure Rate"
          value={pct(data.geolocationFailureRate)}
          icon={MapPinOff}
          iconColor="text-red-500"
        />
        <StatCard
          label="Error Rate"
          value={pct(data.errorRate)}
          icon={Bug}
          iconColor="text-red-500"
        />
        <StatCard
          label="Browse Abandonment"
          value={pct(data.browseAbandonmentRate)}
          icon={LogOut}
          iconColor="text-amber-600"
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">Top Errors</h3>
          </div>
          {data.topErrors.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No errors recorded</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 dark:bg-[#1e2520]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">
                    Message
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">
                    Source
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                {data.topErrors.map((row, i) => (
                  <tr
                    key={`${row.source}-${row.message}-${i}`}
                    className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]"
                  >
                    <td className="px-4 py-2 text-bark-900 dark:text-stone-200 truncate max-w-xs">
                      {row.message}
                    </td>
                    <td className="px-4 py-2 text-stone-600 dark:text-stone-400">{row.source}</td>
                    <td className="px-4 py-2 text-right">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">
              Top Zero-result Locations
            </h3>
          </div>
          {data.topZeroResultLocations.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No zero-result searches</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 dark:bg-[#1e2520]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">
                    Sessions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                {data.topZeroResultLocations.map((row, i) => (
                  <tr
                    key={`${row.locationLabel}-${i}`}
                    className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]"
                  >
                    <td className="px-4 py-2 text-bark-900 dark:text-stone-200">
                      {row.locationLabel}
                    </td>
                    <td className="px-4 py-2 text-right">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
